import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Footprints, MapPinPlus, TriangleAlert, RotateCcw, RotateCw, Minus, Plus, Radar, Rows2 } from 'lucide-react';
import CatalogPicker from './CatalogPicker';
import { extOk } from '../lib/policy';
import SvOverlay from './SvOverlay';
import useSvMiniMap from './useSvMiniMap';
import { svDest, norm360, streetViewEmbedH } from '../lib/svgeo';
import { useVis, pipeCat, fitCat } from '../lib/vis';
import { activeLoggers } from '../hooks/useLoggers';

const clampD = (d) => Math.min(25, Math.max(1, Math.round(d * 2) / 2));

function SvBody({ t, net, project, start, onClose, onPlaced, renderSheet }) {
  const [cam, setCam] = useState([+start.lat, +start.lon]);
  const [heading, setHeading] = useState(0);
  const [dist, setDist] = useState(8);
  const [frame, setFrame] = useState({ cam, h: 0 });
  const [layout, setLayout] = useState('split');
  const [pick, setPick] = useState(false);
  const [sel, setSel] = useState(null);
  const miniRef = useRef(null);
  const vis = useVis();
  const pipes = (project?.pipes || []).filter((p) => vis[pipeCat(p)]);
  const fittings = (project?.fittings || []).filter((f) => vis[fitCat(f)]);
  const loggers = vis.loggers ? activeLoggers(project) : [];
  useEffect(() => { const id = setTimeout(() => setFrame({ cam, h: heading }), 600); return () => clearTimeout(id); }, [cam, heading]);
  const setAim = (h, d) => { setHeading(norm360(Math.round(h))); setDist(clampD(d)); };
  useSvMiniMap(miniRef, { cam, heading, dist, pipes, fittings, layout, setCam, setAim, onSel: setSel });
  const target = svDest(cam, heading, dist);
  const place = (it) => {
    net.placeAt(it, target);
    setPick(false);
    toast.success(`${t.svPlaced} · ${dist.toFixed(1).replace('.', ',')} m / ${heading}°`);
    onPlaced?.(target);
  };
  const rot = (d) => setHeading((h) => norm360(h + d));
  const lat = cam[0].toFixed(7), lon = cam[1].toFixed(7);
  const src = streetViewEmbedH(frame.cam[0].toFixed(7), frame.cam[1].toFixed(7), frame.h);
  return (
    <div className={`sv-modal svl-${layout}`} data-testid="streetview-modal">
      <div className="sv-head">
        <span className="sv-title"><Footprints size={16} /> <b>Street View 360°</b> <small data-testid="streetview-coords">{lat}, {lon}</small></span>
        <button className="btn btn-outline btn-sm sv-lay" data-testid="streetview-layout-btn" onClick={() => setLayout(layout === 'split' ? 'radar' : 'split')}>{layout === 'split' ? <><Radar size={15} /> {t.svRadar}</> : <><Rows2 size={15} /> {t.svSplit}</>}</button>
        <button className="btn btn-red sv-close" data-testid="streetview-close-btn" onClick={onClose}><X size={18} /> {t.svClose}</button>
      </div>
      <div className="sv-split">
        <div className="sv-view">
          <iframe key={src} className="sv-frame" title="Street View" data-testid="streetview-iframe" src={src} allow="accelerometer; gyroscope; fullscreen" allowFullScreen loading="eager" referrerPolicy="no-referrer-when-downgrade" />
          <SvOverlay t={t} cam={cam} heading={heading} pipes={pipes} fittings={fittings} loggers={loggers} onSel={(s) => (s.kind === 'logger' ? toast.info(loggers.find((l) => l.id === s.id)?.name) : setSel(s))} />
          <span className="sv-cross" data-testid="streetview-crosshair" />
          <span className="sv-hud" data-testid="streetview-heading">🧭 {heading}° · 🎯 {dist.toFixed(1).replace('.', ',')} m</span>
        </div>
        <div className="sv-mini" ref={miniRef} data-testid="streetview-minimap" />
      </div>
      <div className="sv-ctrl" data-testid="streetview-controls">
        <button className="btn btn-dark btn-sm" data-testid="sv-rot-l15" onClick={() => rot(-15)}><RotateCcw size={15} /> 15°</button>
        <button className="btn btn-dark btn-sm" data-testid="sv-rot-l5" onClick={() => rot(-5)}>◄ 5°</button>
        <button className="btn btn-dark btn-sm" data-testid="sv-rot-r5" onClick={() => rot(5)}>5° ►</button>
        <button className="btn btn-dark btn-sm" data-testid="sv-rot-r15" onClick={() => rot(15)}>15° <RotateCw size={15} /></button>
        <span className="sv-dist">
          <button className="icon-btn" data-testid="sv-dist-minus" onClick={() => setDist((d) => clampD(d - 0.5))}><Minus size={15} /></button>
          <input type="range" min="1" max="25" step="0.5" value={dist} data-testid="sv-dist-slider" onChange={(e) => setDist(+e.target.value)} />
          <button className="icon-btn" data-testid="sv-dist-plus" onClick={() => setDist((d) => clampD(d + 0.5))}><Plus size={15} /></button>
          <b data-testid="sv-dist-value">{dist.toFixed(1).replace('.', ',')} m</b>
        </span>
      </div>
      {pick && <div className="sv-picker" data-testid="streetview-picker"><CatalogPicker t={t} kind="point" testid="sv-fit-picker" onPick={(it) => place(it)} /></div>}
      {net && (
        <div className="sv-actions">
          <button className="btn btn-proj" data-testid="streetview-place-fitting-btn" onClick={() => setPick(!pick)}><MapPinPlus size={17} /> 🎯 {t.svPlaceFitting}</button>
          <button className="btn btn-red" data-testid="streetview-mark-leak-btn" onClick={() => place(null)}><TriangleAlert size={17} /> 🎯 {t.svMarkLeak}</button>
        </div>
      )}
      <p className="sv-hint">{t.svSyncHint} {t.svNoImagery}</p>
      {sel && renderSheet?.(sel, () => setSel(null))}
    </div>
  );
}

export default function StreetViewModal(props) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const on = (e) => { if (!extOk('street')) { window.dispatchEvent(new Event('giss-kritis-block')); return; } setPos(e.detail); };
    const esc = (e) => e.key === 'Escape' && setPos(null);
    window.addEventListener('giss-streetview', on);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('giss-streetview', on); window.removeEventListener('keydown', esc); };
  }, []);
  if (!pos) return null;
  return createPortal(<SvBody key={`${pos.lat},${pos.lon}`} {...props} start={pos} onClose={() => setPos(null)} />, document.body);
}
